import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import styles from './CountrySelector.module.css';
import { useStores } from '../../../../hooks/useStore';
import { CountryModel } from '../../../../models/locations';
import {
  CreateCountryRequest,
  UpdateCountryRequest,
} from '../../../../api/reviews/dto/LocationsRequest.dto';
import { Button } from '../../../../components/UI/Button/Button';
import { Modal } from '../../../../components/UI/Modal/Modal';

interface CountrySelectorProps {
  value?: string;
  onChange: (selectedCountryId: string) => void;
  placeholder?: string;
}

export const CountrySelector: React.FC<CountrySelectorProps> = observer(
  ({ value, onChange, placeholder = 'Выберите страну' }) => {
    const { userStore } = useStores();
    const [countries, setCountries] = useState<CountryModel[]>([]);
    const [loading, setLoading] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [editingCountry, setEditingCountry] = useState<CountryModel | null>(
      null,
    );
    const [editLoading, setEditLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [newCountry, setNewCountry] = useState<{
      name_ru: string;
      name_en: string;
      iso2: string;
    }>({
      name_ru: '',
      name_en: '',
      iso2: '',
    });

    useEffect(() => {
      loadCountries();
    }, []);

    // Вспомогательные функции для работы с переводами
    const getTranslation = (
      translations: (typeof countries)[0]['translations'],
      locale: 'ru' | 'en',
    ) => {
      return translations.find((t) => t.locale === locale);
    };

    const updateTranslation = (
      country: CountryModel,
      locale: 'ru' | 'en',
      name: string,
    ): CountryModel => {
      return {
        ...country,
        translations: country.translations.map((t) =>
          t.locale === locale ? { ...t, name } : t,
        ),
      };
    };

    const loadCountries = async () => {
      setLoading(true);
      try {
        const response =
          await userStore.mainStore.apiClient.reviews.getCountries();
        if (response.success && response.body) {
          setCountries(response.body.countries);
        }
      } catch (error) {
        // Ошибка загрузки стран
      } finally {
        setLoading(false);
      }
    };

    const handleCreateCountry = async () => {
      if (!newCountry.name_ru || !newCountry.name_en || !newCountry.iso2) {
        return;
      }

      setCreateLoading(true);
      try {
        const countryData: CreateCountryRequest = {
          iso2: newCountry.iso2,
          translations: [
            {
              locale: 'ru',
              name: newCountry.name_ru,
            },
            {
              locale: 'en',
              name: newCountry.name_en,
            },
          ],
        };

        const response =
          await userStore.mainStore.apiClient.reviews.createCountry(
            countryData,
          );
        if (response.success && response.body) {
          const createdCountry = response.body;
          setCountries((prev) => [...prev, createdCountry]);
          onChange(createdCountry.id);
          setShowCreateModal(false);
          setNewCountry({
            name_ru: '',
            name_en: '',
            iso2: '',
          });
        }
      } catch (error) {
        // Ошибка создания страны
      } finally {
        setCreateLoading(false);
      }
    };

    const handleEditCountry = async () => {
      if (!editingCountry || !editingCountry.iso2) {
        return;
      }

      const ruTranslation = getTranslation(editingCountry.translations, 'ru');
      const enTranslation = getTranslation(editingCountry.translations, 'en');

      if (!ruTranslation?.name || !enTranslation?.name) {
        return;
      }

      setEditLoading(true);
      try {
        const updateData: UpdateCountryRequest = {
          iso2: editingCountry.iso2,
          translations: [
            {
              locale: 'ru',
              name: ruTranslation.name,
            },
            {
              locale: 'en',
              name: enTranslation.name,
            },
          ],
        };
        const response =
          await userStore.mainStore.apiClient.reviews.updateCountry(
            editingCountry.id,
            updateData,
          );
        if (response.success && response.body) {
          const updatedCountry = response.body;
          setCountries((prev) =>
            prev.map((c) => (c.id === updatedCountry.id ? updatedCountry : c)),
          );
          setEditingCountry(null);
        }
      } catch (error) {
        // Ошибка редактирования страны
      } finally {
        setEditLoading(false);
      }
    };
    const handleDeleteCountry = async (countryId: string) => {
      if (!window.confirm('Вы уверены, что хотите удалить эту страну?')) {
        return;
      }

      setDeleteLoading(true);
      try {
        const response =
          await userStore.mainStore.apiClient.reviews.deleteCountry(countryId);
        if (response.success) {
          setCountries((prev) => prev.filter((c) => c.id !== countryId));
          if (value === countryId) {
            onChange('');
          }
        }
      } catch (error) {
        // Ошибка удаления страны
      } finally {
        setDeleteLoading(false);
      }
    };

    const startEditCountry = (country: CountryModel) => {
      setEditingCountry({ ...country });
    };

    const getCountryName = (
      country: CountryModel,
      locale: 'ru' | 'en' = 'ru',
    ) => {
      const translation = country.translations.find((t) => t.locale === locale);
      return translation?.name || country.iso2;
    };

    const selectedCountry = countries.find((c) => c.id === value);

    return (
      <div className={styles.countrySelector}>
        <div className={styles.selectRow}>
          <select
            className={styles.select}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={loading}
          >
            <option value="">{loading ? 'Загрузка...' : placeholder}</option>
            {countries.map((country) => (
              <option key={country.id} value={country.id}>
                {getCountryName(country)} ({country.iso2})
              </option>
            ))}
          </select>
          <Button
            type="button"
            appearance="link"
            onClick={() => setShowCreateModal(true)}
            className={styles.addButton}
            title="Добавить страну"
          >
            +
          </Button>
        </div>

        {selectedCountry && (
          <div className={styles.selectedInfo}>
            <div className={styles.countryInfo}>
              <small>
                RU: {getCountryName(selectedCountry, 'ru')} | EN:{' '}
                {getCountryName(selectedCountry, 'en')} | {selectedCountry.iso2}
              </small>
            </div>
            <div className={styles.countryActions}>
              <Button
                type="button"
                appearance="link"
                onClick={() => startEditCountry(selectedCountry)}
                className={styles.actionButton}
              >
                ✏️ Редактировать
              </Button>
              <Button
                type="button"
                appearance="link"
                onClick={() => handleDeleteCountry(selectedCountry.id)}
                disabled={deleteLoading}
                className={styles.deleteButton}
              >
                {deleteLoading ? '⏳' : '🗑️'} Удалить
              </Button>
            </div>
          </div>
        )}

        <Modal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          title="Создать новую страну"
        >
          <div className={styles.createForm}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Название страны (RU)*</label>
              <input
                type="text"
                className={styles.input}
                value={newCountry.name_ru}
                onChange={(e) =>
                  setNewCountry((prev) => ({
                    ...prev,
                    name_ru: e.target.value,
                  }))
                }
                placeholder="Введите название на русском"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Country Name (EN)*</label>
              <input
                type="text"
                className={styles.input}
                value={newCountry.name_en}
                onChange={(e) =>
                  setNewCountry((prev) => ({
                    ...prev,
                    name_en: e.target.value,
                  }))
                }
                placeholder="Enter country name in English"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>ISO2 код*</label>
              <input
                type="text"
                className={styles.input}
                value={newCountry.iso2}
                onChange={(e) =>
                  setNewCountry((prev) => ({
                    ...prev,
                    iso2: e.target.value.toUpperCase(),
                  }))
                }
                placeholder="RU, US, DE..."
                maxLength={2}
                required
              />
            </div>

            <div className={styles.modalActions}>
              <Button
                type="button"
                appearance="link"
                onClick={() => setShowCreateModal(false)}
              >
                Отмена
              </Button>
              <Button
                type="button"
                appearance="auth"
                onClick={handleCreateCountry}
                disabled={
                  !newCountry.name_ru ||
                  !newCountry.name_en ||
                  !newCountry.iso2 ||
                  createLoading
                }
              >
                {createLoading ? 'Создание...' : 'Создать страну'}
              </Button>
            </div>
          </div>
        </Modal>

        <Modal
          isOpen={!!editingCountry}
          onClose={() => setEditingCountry(null)}
          title="Редактировать страну"
        >
          {editingCountry && (
            <div className={styles.createForm}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Название страны (RU)*</label>
                <input
                  type="text"
                  className={styles.input}
                  value={
                    getTranslation(editingCountry.translations, 'ru')?.name ||
                    ''
                  }
                  onChange={(e) =>
                    setEditingCountry((prev) =>
                      prev
                        ? updateTranslation(prev, 'ru', e.target.value)
                        : null,
                    )
                  }
                  placeholder="Введите название на русском"
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Country Name (EN)*</label>
                <input
                  type="text"
                  className={styles.input}
                  value={
                    getTranslation(editingCountry.translations, 'en')?.name ||
                    ''
                  }
                  onChange={(e) =>
                    setEditingCountry((prev) =>
                      prev
                        ? updateTranslation(prev, 'en', e.target.value)
                        : null,
                    )
                  }
                  placeholder="Enter country name in English"
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>ISO2 код*</label>
                <input
                  type="text"
                  className={styles.input}
                  value={editingCountry.iso2}
                  onChange={(e) =>
                    setEditingCountry((prev) =>
                      prev
                        ? {
                            ...prev,
                            iso2: e.target.value.toUpperCase(),
                          }
                        : null,
                    )
                  }
                  placeholder="RU, US, DE..."
                  maxLength={2}
                  required
                />
              </div>

              <div className={styles.modalActions}>
                <Button
                  type="button"
                  appearance="link"
                  onClick={() => setEditingCountry(null)}
                >
                  Отмена
                </Button>
                <Button
                  type="button"
                  appearance="auth"
                  onClick={handleEditCountry}
                  disabled={
                    !getTranslation(editingCountry.translations, 'ru')?.name ||
                    !getTranslation(editingCountry.translations, 'en')?.name ||
                    !editingCountry.iso2 ||
                    editLoading
                  }
                >
                  {editLoading ? 'Сохранение...' : 'Сохранить изменения'}
                </Button>
              </div>
            </div>
          )}
        </Modal>
      </div>
    );
  },
);

export default CountrySelector;
